import {
  addCopilotFrontendToolCallIdToElement,
  applyCopilotHighlightLayout,
  clearCopilotHighlightLayout,
  COPILOT_ACTION_HIGHLIGHT_ATTRIBUTE,
  COPILOT_ACTION_HIGHLIGHT_ANIMATION_DURATION_MS,
  COPILOT_ACTION_RING_WIDTH_PX,
  COPILOT_ELEMENT_HIGHLIGHT_LOOP_DURATION_MS,
  COPILOT_FOCUS_HIGHLIGHT_ATTRIBUTE,
  COPILOT_FOCUS_PULSE_ITERATIONS,
  COPILOT_FOCUS_RING_WIDTH_PX,
  type CopilotElementHighlightEffect,
  scheduleCopilotElementHighlightCleanup
} from './copilotElementHighlight'
import { ensureCopilotActionHighlightStyles, ensureCopilotFocusHighlightStyles } from './copilotElementHighlightStyles'
import type { Theme } from '@precis-digital/kurama/theme'

interface ApplyCopilotHighlightOptions {
  highlightId: string
  toolCallId: string
  activeCleanups: Set<() => void>
}

/**
 * AI-triggered highlight when a frontend tool completes.
 * Applies the rotating AI gradient action ring and schedules automatic cleanup.
 */
export const applyCopilotActionHighlight = (
  element: HTMLElement,
  { highlightId, toolCallId, activeCleanups }: ApplyCopilotHighlightOptions,
  theme: Theme
): CopilotElementHighlightEffect => {
  ensureCopilotActionHighlightStyles(theme)

  addCopilotFrontendToolCallIdToElement(element, toolCallId)
  element.setAttribute(COPILOT_ACTION_HIGHLIGHT_ATTRIBUTE, highlightId)

  const layoutState = applyCopilotHighlightLayout(element, 'action', COPILOT_ACTION_RING_WIDTH_PX)

  element.style.setProperty(
    '--copilot-action-highlight-duration',
    `${COPILOT_ACTION_HIGHLIGHT_ANIMATION_DURATION_MS}ms`
  )

  return scheduleCopilotElementHighlightCleanup(
    () => {
      element.removeAttribute(COPILOT_ACTION_HIGHLIGHT_ATTRIBUTE)
      clearCopilotHighlightLayout(element, 'action', layoutState)
      element.style.removeProperty('--copilot-action-highlight-duration')
    },
    activeCleanups,
    COPILOT_ACTION_HIGHLIGHT_ANIMATION_DURATION_MS
  )
}

/**
 * User-initiated focus highlight with pulsing shadow.
 * Used when replaying highlights from thinking signatures or explicit highlight requests.
 */
export const applyCopilotFocusHighlight = (
  element: HTMLElement,
  { highlightId, toolCallId, activeCleanups }: ApplyCopilotHighlightOptions,
  theme: Theme
): CopilotElementHighlightEffect => {
  ensureCopilotFocusHighlightStyles(theme)

  addCopilotFrontendToolCallIdToElement(element, toolCallId)
  element.setAttribute(COPILOT_FOCUS_HIGHLIGHT_ATTRIBUTE, highlightId)

  const layoutState = applyCopilotHighlightLayout(element, 'focus', COPILOT_FOCUS_RING_WIDTH_PX)

  element.style.setProperty('--copilot-focus-pulse-duration', `${COPILOT_ELEMENT_HIGHLIGHT_LOOP_DURATION_MS}ms`)
  element.style.setProperty('--copilot-focus-pulse-iterations', String(COPILOT_FOCUS_PULSE_ITERATIONS))

  return scheduleCopilotElementHighlightCleanup(() => {
    element.removeAttribute(COPILOT_FOCUS_HIGHLIGHT_ATTRIBUTE)
    clearCopilotHighlightLayout(element, 'focus', layoutState)
    element.style.removeProperty('--copilot-focus-pulse-duration')
    element.style.removeProperty('--copilot-focus-pulse-iterations')
  }, activeCleanups)
}
